/**
 * The single place application code calls to record an event.
 *
 * Two tools are wired underneath, because they answer different questions:
 *
 *   Clarity  — why. Session recordings, heatmaps, rage clicks.
 *   GA4      — how many, from where, when. Trends, sources, conversion rates.
 *
 * Call sites import from here rather than from either transport, so adding or
 * removing a tool later is a change to this file alone instead of to every page
 * that happens to track something.
 *
 * GA4 event naming follows Google's recommended set wherever one exists
 * (`login`, `sign_up`, `add_to_cart`), because those feed GA4's built-in
 * reports; inventing custom names for them would leave those reports empty.
 * Everything else is a custom event. Clarity has no such vocabulary, so it
 * keeps the plainer names it already used.
 *
 * Neither tool ever receives a name, email or phone number. Identifiers are the
 * Mongo id only — see the note in ga.js about GA4's terms on PII.
 */

import * as clarity from './clarity';
import * as ga from './ga';

// ─── Identity ────────────────────────────────────────────────────────────────

export const identify = (userId, { role } = {}) => {
  clarity.identify(userId, { role });
  ga.setUserId(userId);
  if (role) ga.setUserProperty('role', role);
};

// ─── Auth ────────────────────────────────────────────────────────────────────

export const trackLogin = (role, userId) => {
  clarity.trackLogin(role, userId);
  // `method` is the parameter GA4's own login report reads.
  ga.trackEvent('login', { method: role });
  if (userId) {
    ga.setUserId(userId);
    ga.setUserProperty('role', role);
  }
};

export const trackSignup = (role, userId) => {
  clarity.trackSignup(role, userId);
  ga.trackEvent('sign_up', { method: role });
  if (userId) {
    ga.setUserId(userId);
    ga.setUserProperty('role', role);
  }
};

export const trackSignupStep = (role, step) => {
  clarity.trackSignupStep(role, step);
  // Custom: GA4 has no recommended event for a step inside signup.
  ga.trackEvent(`sign_up_${step}`, { method: role });
};

export const trackLogout = (role) => {
  clarity.trackLogout(role);
  ga.trackEvent('logout', { method: role });
};

// ─── Commerce ────────────────────────────────────────────────────────────────

export const trackTryOn = (outcome, productId) => {
  clarity.trackTryOn(outcome, productId);
  ga.trackEvent(`virtual_tryon_${outcome}`, { item_id: productId });
};

export const trackAddToCart = (outcome, productId) => {
  clarity.trackAddToCart(outcome, productId);
  // Only the successful case is GA4's recommended `add_to_cart`; reporting a
  // blocked or failed attempt under that name would inflate the metric the
  // e-commerce reports are built on.
  if (outcome === 'success') {
    ga.trackEvent('add_to_cart', { item_id: productId });
  } else {
    ga.trackEvent(`add_to_cart_${outcome}`, { item_id: productId });
  }
};

export const trackPayment = (outcome, method) => {
  clarity.trackPayment(outcome, method);
  ga.trackEvent(`payment_${outcome}`, { payment_type: method });
};

export const trackOrderCancel = (outcome, reason) => {
  clarity.trackOrderCancel(outcome, reason);
  ga.trackEvent(`order_cancel_${outcome}`, {
    cancel_reason: outcome === 'confirmed' ? reason : undefined
  });
};

// ─── Navigation ──────────────────────────────────────────────────────────────

/** Clarity tracks route changes itself; only GA4 needs telling. */
export const trackPageView = (path, title) => ga.trackPageView(path, title);

const analytics = {
  identify,
  trackLogin,
  trackSignup,
  trackSignupStep,
  trackLogout,
  trackTryOn,
  trackAddToCart,
  trackPayment,
  trackOrderCancel,
  trackPageView
};

export default analytics;
